import {
    ApiQuery,
    BasicFilter,
    Filter,
    FilterMetadata,
    FilterOperators,
    SelectOptionValue,
} from "~/modules/filters/types";
import { AssetColumns } from "~/modules/assets/types";
import { MultiSelectOption } from "~/components/MultiSelect";

// // // //

// Pass `counts` to attach an asset count to each option (missing ids count as 0)
export function getMultiSelectOptions(
    filterMetadata: FilterMetadata,
    counts?: Record<string, number>,
): MultiSelectOption[] {
    return (filterMetadata.selectOptions ?? []).map((option) => ({
        value: String(option.value),
        label: option.content.label,
        icon: option.content.icon,
        count: counts ? (counts[String(option.value)] ?? 0) : undefined,
    }));
}

export function getMultiSelectValue(value: unknown): string[] {
    return Array.isArray(value) ? value.map(String) : [];
}

// Maps selected string values back to the metadata's original option values
export function getFilterValue(
    filterMetadata: FilterMetadata,
    selected: string[],
): SelectOptionValue[] {
    const options = filterMetadata.selectOptions ?? [];
    return selected.map(
        (value) =>
            options.find((option) => String(option.value) === value)?.value ??
            value,
    );
}

// // // //
// Redemption terms
// The raw primary_market__redemption_frequency values are grouped into a
// shorter list of options - selecting an option filters on all of its values

// Raw primary_market__redemption_frequency values (what the API filters on)
enum RedemptionFrequencies {
    Instant = "Instant",
    ThirtyMinutesIfUSMarketsOpen = "30 minutes if US markets are open. Otherwise next trading day.",
    Daily = "Daily",
    USBankingDaily = "U.S. Banking Daily",
    Weekly = "Weekly",
    Monthly30Day = "Monthly (30d)",
    MonthlyCalendar = "Monthly (Calendar)",
    Quarterly = "Quarterly",
    Yearly360Day = "Yearly (360d)",
    OnlyAtMaturity = "Only at maturity",
}

interface RedemptionTermOption {
    value: string;
    label: string;
    frequencies: RedemptionFrequencies[];
}

const REDEMPTION_TERM_OPTIONS: RedemptionTermOption[] = [
    {
        value: "instant",
        label: "Instant",
        frequencies: [
            RedemptionFrequencies.ThirtyMinutesIfUSMarketsOpen,
            RedemptionFrequencies.Instant,
        ],
    },
    {
        value: "daily",
        label: "Daily",
        frequencies: [
            RedemptionFrequencies.Daily,
            RedemptionFrequencies.USBankingDaily,
        ],
    },
    {
        value: "monthly",
        label: "Monthly",
        frequencies: [
            RedemptionFrequencies.Monthly30Day,
            RedemptionFrequencies.MonthlyCalendar,
        ],
    },
    {
        value: "quarterly",
        label: "Quarterly",
        frequencies: [RedemptionFrequencies.Quarterly],
    },
    {
        value: "weekly",
        label: "Weekly",
        frequencies: [RedemptionFrequencies.Weekly],
    },
    {
        value: "yearly",
        label: "Yearly",
        frequencies: [RedemptionFrequencies.Yearly360Day],
    },
    {
        value: "maturity-only",
        label: "Maturity only",
        frequencies: [RedemptionFrequencies.OnlyAtMaturity],
    },
];

// Options with counts summed across each option's frequencies
export function getRedemptionTermMultiSelectOptions(
    redemptionFrequencyCounts: Record<string, number>,
): MultiSelectOption[] {
    return REDEMPTION_TERM_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        count: option.frequencies.reduce(
            (sum, frequency) =>
                sum + (redemptionFrequencyCounts[frequency] ?? 0),
            0,
        ),
    }));
}

// Filter value (raw frequencies) -> selected options (all frequencies present)
export function getSelectedRedemptionTermOptions(value: unknown): string[] {
    const selectedFrequencies = new Set(getMultiSelectValue(value));
    return REDEMPTION_TERM_OPTIONS.filter((option) =>
        option.frequencies.every((frequency) =>
            selectedFrequencies.has(frequency),
        ),
    ).map((option) => option.value);
}

// Selected options -> filter value (raw frequencies)
export function getRedemptionFrequencyFilterValue(
    selectedOptions: string[],
): RedemptionFrequencies[] {
    return REDEMPTION_TERM_OPTIONS.filter((option) =>
        selectedOptions.includes(option.value),
    ).flatMap((option) => option.frequencies);
}

// // // //
// 30D APY range
// FilterFormContext keeps one filter per field, so a min + max on apy_30_day
// can't be expressed through FilterFormField - instead the range lives in the
// query as a nested `and` group: { and: [apy_30_day gte min, apy_30_day lte max] }.
// FilterFormContext leaves nested groups untouched, and resetFilters clears it.

const APY_RANGE_MIN = 0;

// Same as the /asset-screener page - 2% of headroom above the highest APY
export function getApyRangeMax(apy30DayValues: number[]): number {
    if (apy30DayValues.length === 0) return 1;
    return Math.ceil(Math.max(...apy30DayValues) + 2);
}

function isApyRangeFilter(filter: Filter): boolean {
    return (
        "filters" in filter &&
        Array.isArray(filter.filters) &&
        filter.filters.length > 0 &&
        (filter.filters as BasicFilter[]).every(
            (each) => each.field === AssetColumns.apy30Day,
        )
    );
}

export function getTopLevelFilters(query: ApiQuery): Filter[] {
    if (!query.filter || !("filters" in query.filter)) return [];
    return (query.filter.filters ?? []) as Filter[];
}

export function getApyRange(
    query: ApiQuery,
    rangeMax: number,
): [number, number] {
    const rangeFilter = getTopLevelFilters(query).find(isApyRangeFilter);
    const filters = (rangeFilter?.filters ?? []) as BasicFilter[];
    const min = filters.find(
        (each) => each.operator === FilterOperators.GreaterThanOrEqual,
    )?.value;
    const max = filters.find(
        (each) => each.operator === FilterOperators.LessThanOrEqual,
    )?.value;
    return [
        typeof min === "number" ? min : APY_RANGE_MIN,
        typeof max === "number" ? max : rangeMax,
    ];
}

export function setApyRange(
    query: ApiQuery,
    range: [number, number],
    rangeMax: number,
): ApiQuery {
    const [min, max] = range;
    const rangeFilters: BasicFilter[] = [];
    if (min > APY_RANGE_MIN) {
        rangeFilters.push({
            field: AssetColumns.apy30Day,
            operator: FilterOperators.GreaterThanOrEqual,
            value: min,
        });
    }
    if (max < rangeMax) {
        rangeFilters.push({
            field: AssetColumns.apy30Day,
            operator: FilterOperators.LessThanOrEqual,
            value: max,
        });
    }

    const filters = getTopLevelFilters(query).filter(
        (each) => !isApyRangeFilter(each),
    );
    if (rangeFilters.length > 0) {
        filters.push({
            operator: FilterOperators.And,
            filters: rangeFilters,
        } as Filter);
    }

    return {
        ...query,
        filter: {
            operator: FilterOperators.And,
            filters,
        } as Filter,
    };
}
